import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslations } from 'src/contexts/TranslationContext';
import RobotsNoIndex from 'src/components/seo/RobotsNoIndex';

interface ArchiveDateProps {
    lang?: string;
}

const ArchiveDate: React.FC<ArchiveDateProps> = () => {
    const location = useLocation();
    const navigate = useNavigate();
    // const { type: typeFromPath } = useParams<{ type: string }>();
    const { language, getUrlLanguagePrefix, translations: t } = useTranslations();
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [minTrendingScore, setMinTrendingScore] = useState<number>(60);
    const [error, setError] = useState<string | null>(null);

    // First available date: March 25, 2025
    const FIRST_AVAILABLE_DATE = new Date(2025, 3, 4); // Month is 0-based
    const minDateString = '2025-04-04';
    
    // Max date is today
    const TODAY = new Date();
    const maxDateString = TODAY.toISOString().split('T')[0];

    // Get subtype from query parameters
    const searchParams = new URLSearchParams(location.search);
    const subtype = searchParams.get('subtype') || undefined;

    // Type is no longer used, but keeping for backward compatibility
    // const type = undefined;

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedDate = new Date(e.target.value + 'T00:00:00'); // Add time to ensure correct timezone handling
        if (selectedDate < FIRST_AVAILABLE_DATE) {
            setError('Please select a date on or after March 25, 2025');
            return;
        }
        if (selectedDate > TODAY) {
            setError('Please select a date no later than today');
            return;
        }
        setError(null);
        setSelectedDate(e.target.value);
    };

    const handleTrendingScoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setMinTrendingScore(parseInt(e.target.value, 10));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedDate) return;

        const selectedDateObj = new Date(selectedDate + 'T00:00:00'); // Add time to ensure correct timezone handling
        if (selectedDateObj < FIRST_AVAILABLE_DATE) {
            setError('Please select a date on or after March 25, 2025');
            return;
        }
        if (selectedDateObj > TODAY) {
            setError('Please select a date no later than today');
            return;
        }

        // Format date as YYYYMMDD - use the date string directly to avoid timezone issues
        const [year, month, day] = selectedDate.split('-');
        const formattedDate = `${year}${month}${day}`;

        const prefix = getUrlLanguagePrefix(language);
        let path = `${prefix}/archive/dates/${formattedDate}`;
        
        // Add query parameters for subtype and minTrendingScore
        const queryParams = new URLSearchParams();
        if (subtype) queryParams.append('subtype', subtype);
        queryParams.append('minTrendingScore', minTrendingScore.toString());
        
        path += `?${queryParams.toString()}`;
        
        navigate(path);
    };

    const handleBack = () => {
        navigate(-1);
    };

    const handleArchiveHome = () => {
        const prefix = getUrlLanguagePrefix(language);
        navigate(`${prefix}/archive`);
    };

    // Get translated subtype name
    const getSubtypeTranslation = (subtype: string) => {
        return t?.navigations?.header?.[subtype.toLowerCase()] ||
            subtype.charAt(0).toUpperCase() + subtype.slice(1);
    };

    // Format the first available date for display
    const formatFirstAvailableDate = () => {
        return FIRST_AVAILABLE_DATE.toLocaleDateString(language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="max-w-4xl mx-auto p-8">
            <RobotsNoIndex />
            <div className="flex items-center justify-between mb-8">
                <button
                    onClick={handleBack}
                    className="flex items-center text-blue-600 hover:text-blue-800"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {t?.navigations?.header?.back || 'Back'}
                </button>
                <button
                    onClick={handleArchiveHome}
                    className="flex items-center text-blue-600 hover:text-blue-800 text-base"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    {t?.navigations?.header?.archiveHome || 'Archive Home'}
                </button>
            </div>

            <div className="mb-8">
                <h1 className="text-3xl font-bold mb-2">
                    {subtype ? getSubtypeTranslation(subtype) : 'All News'}
                </h1>
                <div className="text-gray-600 space-y-2">
                    <p>{t?.navigations?.header?.selectDate || 'Select a date to view news'}</p>
                    <p className="text-sm">
                        First available date: <span className="font-medium">{formatFirstAvailableDate()}</span>
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="mb-8">
                <div className="mb-6">
                    <label htmlFor="date-input" className="block text-sm font-medium text-gray-700 mb-2">
                        {t?.navigations?.header?.date || 'Date'}
                    </label>
                    <input
                        id="date-input"
                        type="date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        min={minDateString}
                        max={maxDateString}
                        className="border rounded p-2 w-full"
                        required
                    />
                </div>

                <div className="mb-6">
                    <div className="flex justify-between mb-2">
                        <label htmlFor="trending-slider" className="block text-sm font-medium text-gray-700">
                            {t?.navigations?.header?.trendingLevel || 'Trending Level'}
                        </label>
                        <span className="text-sm font-medium text-gray-700">{minTrendingScore}</span>
                    </div>
                    <input
                        id="trending-slider"
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={minTrendingScore}
                        onChange={handleTrendingScoreChange}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between mt-1 text-xs text-gray-500">
                        <span>0</span>
                        <span>50</span>
                        <span>100</span>
                    </div>
                </div>

                <button
                    type="submit"
                    className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 w-full"
                    disabled={!selectedDate || !!error}
                >
                    {t?.navigations?.header?.viewNews || 'View News'}
                </button>
            </form>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}
        </div>
    );
};

export default ArchiveDate; 